import React, { useCallback, useEffect, useState } from "react";
import { getPlanningGrid } from "../api";
import CapacityView from "./planning/CapacityView";
import DeadlinesView from "./planning/DeadlinesView";
import PlanningCellDrawer from "./planning/PlanningCellDrawer";
import TriageView from "./planning/TriageView";

const CATEGORIES = [
  { key: "personal", label: "Personal" },
  { key: "corporate", label: "Corporate" },
  { key: "tslips", label: "T-Slips" },
  { key: "other", label: "Other" },
];

const MONTH_OPTIONS = [3, 6, 12];

// Three questions, three views, one filter set. "Who is overloaded", "what is
// due" and "what is stuck" are different questions about the same work, and
// the old page tried to answer all three in one grid of numbers.
const TABS = [
  { key: "capacity", label: "Capacity" },
  { key: "deadlines", label: "Deadlines" },
  { key: "triage", label: "Triage" },
];

// Mirrors the server's clients.views._is_manager, which the planning
// endpoints gate on: a superuser with no employee role passes there, so it
// must pass here too, or they are told "managers only" about an API that
// would happily serve them.
const isManager = (user) =>
  Boolean(
    user &&
      (user.is_superuser ||
        ["MANAGER", "ADMIN"].includes(String(user.role).toUpperCase()))
  );

export default function PlanningPage({ user }) {
  const [tab, setTab] = useState("capacity");
  // Whose deadlines. Lives here rather than inside DeadlinesView because the
  // employee list comes from the grid payload the page already holds.
  const [deadlineEmployee, setDeadlineEmployee] = useState(null);
  const [months, setMonths] = useState(6);
  const [categories, setCategories] = useState([]);
  const [plannedOnly, setPlannedOnly] = useState(false);
  const [grid, setGrid] = useState(null);
  const [openCell, setOpenCell] = useState(null);
  const [error, setError] = useState("");

  const manager = isManager(user);

  // The server decides which rows are in scope for a given filter set — the
  // grid is reloaded whenever months, categories, or planned_only change
  // rather than filtered client-side.
  // Only the visible tab fetches. Deadlines and Triage load their own data;
  // the grid payload is kept in state across tab switches because Triage's
  // bulk controls need its employee list and month options, and Capacity is
  // always the tab a manager arrives on.
  const load = useCallback(() => {
    if (!manager || tab !== "capacity") return;
    const params = { months };
    if (categories.length) params.categories = categories.join(",");
    if (plannedOnly) params.planned_only = 1;
    getPlanningGrid(params)
      .then((data) => {
        setGrid(data);
        setError("");
      })
      .catch(() => setError("Could not load the planning grid."));
  }, [manager, tab, months, categories, plannedOnly]);

  useEffect(() => {
    load();
  }, [load]);

  if (!manager) {
    return <div style={{ padding: 24 }}>Planning is managers only.</div>;
  }

  const toggleCategory = (key) =>
    setCategories((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginTop: 0 }}>Planning</h2>
      <p style={{ color: "#6B7280", marginTop: -8 }}>
        Work assigned ahead, by employee and month. Assigning here does not
        move a task onto the Tasks grid — logging time or moving its status
        does.
      </p>

      <div
        style={{
          display: "flex", gap: 16, alignItems: "center",
          flexWrap: "wrap", margin: "16px 0",
        }}
      >
        <div style={{ display: "flex", gap: 4 }}>
          {MONTH_OPTIONS.map((m) => (
            <button
              key={m}
              onClick={() => setMonths(m)}
              aria-pressed={months === m}
              style={{
                padding: "4px 10px",
                background: months === m ? "#111827" : "#fff",
                color: months === m ? "#fff" : "#374151",
                border: "1px solid #D1D5DB", borderRadius: 6,
              }}
            >
              {m}
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 4 }}>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => toggleCategory(c.key)}
              aria-pressed={categories.includes(c.key)}
              style={{
                padding: "4px 10px",
                background: categories.includes(c.key) ? "#111827" : "#fff",
                color: categories.includes(c.key) ? "#fff" : "#374151",
                border: "1px solid #D1D5DB", borderRadius: 999,
              }}
            >
              {c.label}
            </button>
          ))}
        </div>

        <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <input
            type="checkbox"
            checked={plannedOnly}
            onChange={(e) => setPlannedOnly(e.target.checked)}
          />
          Planned only
        </label>
      </div>

      <div role="tablist" style={{ display: "flex", gap: 4, marginBottom: 16 }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: "6px 14px",
              background: "transparent",
              color: tab === t.key ? "#111827" : "#6B7280",
              border: "none",
              borderBottom: `2px solid ${tab === t.key ? "#111827" : "transparent"}`,
              fontWeight: tab === t.key ? 600 : 400,
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error ? <p style={{ color: "#B91C1C" }}>{error}</p> : null}

      {tab === "capacity" ? (
        grid ? (
          <CapacityView
            grid={grid}
            onCellClick={(rowKey, month) => setOpenCell({ rowKey, month })}
          />
        ) : (
          <p style={{ color: "#6B7280" }}>Loading…</p>
        )
      ) : null}

      {tab === "deadlines" ? (
        <>
          <label style={{ display: "block", marginBottom: 12 }}>
            <span style={{ color: "#6B7280", marginRight: 8 }}>Assigned to</span>
            <select
              data-testid="deadlines-employee"
              value={deadlineEmployee == null ? "" : deadlineEmployee}
              onChange={(e) =>
                setDeadlineEmployee(
                  e.target.value === "" ? null : parseInt(e.target.value, 10)
                )
              }
            >
              <option value="">Everyone</option>
              {(grid ? grid.employees : []).map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </label>
          <DeadlinesView
            months={months}
            categories={categories}
            employee={deadlineEmployee}
          />
        </>
      ) : null}

      {tab === "triage" ? (
        <TriageView
          categories={categories}
          monthOptions={grid ? grid.months : []}
          employees={grid ? grid.employees : []}
          // A bulk action here changes what the grid would show, so the grid
          // is refetched rather than left to go quietly stale behind the tab.
          onChanged={load}
        />
      ) : null}

      {openCell ? (
        <PlanningCellDrawer
          rowKey={openCell.rowKey}
          month={openCell.month}
          employees={grid ? grid.employees : []}
          monthOptions={grid ? grid.months : []}
          // The drawer must resolve the cell under the same filters the
          // grid counted it under, or the drill-down contradicts the
          // number on screen.
          categories={categories}
          plannedOnly={plannedOnly}
          onClose={() => setOpenCell(null)}
          onAssigned={() => {
            setOpenCell(null);
            load();
          }}
        />
      ) : null}
    </div>
  );
}
